'use client';

import { useTranslations } from 'next-intl';
import { GmAwardCard } from '@/components/entities/gamification/GmAwardCard';
import { GmRewardsSkeleton } from '@/components/entities/gamification/skeletons/GmRewardsSkeleton';
import { Icon } from '@/components/entities/Sprite';
import { STATUS_REWARD_BENEFITS } from '@/components/features/gamification/model/constants/statusRewardData';
import { useOpenGamificationRewardAction } from '@/components/features/gamification/statuses/hooks/useOpenGamificationRewardAction';
import { NotificationBadge } from '@/components/shared/NotificationBadge';
import { useGamificationRewardsGridData } from '../hooks/useGamificationRewardsGridData';

interface GamificationRewardsGridProps {
  countOfGamificationRewards: number;
}

export const GamificationRewardsGrid = ({ countOfGamificationRewards }: GamificationRewardsGridProps) => {
  const t = useTranslations('gamification');
  const { cards, isLoading } = useGamificationRewardsGridData();
  const openGamificationRewardAction = useOpenGamificationRewardAction();

  if (isLoading) {
    return <GmRewardsSkeleton count={STATUS_REWARD_BENEFITS.length} />;
  }

  return (
    <section className="flex flex-col gap-3 p-4 glass-stroke-border gm-award-wrapper-gradient max-md:rounded-2xl max-md:shadow-card md:bg-none md:p-0 md:before:hidden [&>*:first-child]:w-fit">
      <div className="flex min-h-5 items-center justify-between gap-2">
        <h2 className="text-sm font-semibold leading-5 text-white md:text-base">
          {t('rewards')}
        </h2>

        {
          countOfGamificationRewards > 0 && (
            <NotificationBadge className="md:hidden">
              {countOfGamificationRewards}
            </NotificationBadge>
          )
        }

        <Icon name="reward" className="block size-6 -order-1 md:hidden" />
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {cards.map(({ action, disabled, reward }) => (
          <GmAwardCard
            key={reward.type}
            className="h-35"
            reward={reward}
            disabled={disabled}
            onClick={action ? () => openGamificationRewardAction(action) : undefined}
          />
        ))}
      </div>
    </section>
  );
};
